'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useRouter } from 'next/navigation';
import AppLayout from '@/components/AppLayout';
import {
  getWorldCupStandings,
  getUpcomingFixtures,
  getFlag,
  type ApiStanding,
  type LiveFixture,
} from '@/lib/api-football';
import {
  ArrowLeft,
  Users,
  BarChart2,
  Calendar,
  History,
  TrendingUp,
  Shield,
  Target,
  Swords,
  RefreshCw,
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface TeamStats {
  played: number;
  wins: number;
  draws: number;
  losses: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDiff: number;
  points: number;
  group: string;
  rank: number;
}

interface SquadPlayer {
  id: number;
  name: string;
  number: number;
  pos: string;
}

const POSITION_ORDER: Record<string, number> = { G: 0, D: 1, M: 2, F: 3 };
const POSITION_LABEL: Record<string, string> = { G: 'Goalkeeper', D: 'Defender', M: 'Midfielder', F: 'Forward' };

// Mock squad data keyed by team name (real squad API requires paid plan)
const MOCK_SQUADS: Record<string, SquadPlayer[]> = {
  default: [
    { id: 1, name: 'M. Neuer', number: 1, pos: 'G' },
    { id: 2, name: 'T. Alexander-Arnold', number: 2, pos: 'D' },
    { id: 3, name: 'R. Dias', number: 3, pos: 'D' },
    { id: 4, name: 'V. van Dijk', number: 4, pos: 'D' },
    { id: 5, name: 'A. Davies', number: 5, pos: 'D' },
    { id: 6, name: 'J. Bellingham', number: 8, pos: 'M' },
    { id: 7, name: 'F. de Jong', number: 21, pos: 'M' },
    { id: 8, name: 'K. De Bruyne', number: 7, pos: 'M' },
    { id: 9, name: 'V. Osimhen', number: 9, pos: 'F' },
    { id: 10, name: 'K. Mbappé', number: 10, pos: 'F' },
    { id: 11, name: 'E. Haaland', number: 11, pos: 'F' },
    { id: 12, name: 'A. Onana', number: 16, pos: 'G' },
    { id: 13, name: 'J. Gvardiol', number: 24, pos: 'D' },
    { id: 14, name: 'W. Saliba', number: 12, pos: 'D' },
    { id: 15, name: 'P. Foden', number: 47, pos: 'M' },
    { id: 16, name: 'B. Fernandes', number: 8, pos: 'M' },
    { id: 17, name: 'L. Goretzka', number: 14, pos: 'M' },
    { id: 18, name: 'R. Leão', number: 17, pos: 'F' },
    { id: 19, name: 'M. Salah', number: 11, pos: 'F' },
    { id: 20, name: 'L. Martínez', number: 22, pos: 'F' },
    { id: 21, name: 'Y. Sommer', number: 1, pos: 'G' },
    { id: 22, name: 'D. Carvajal', number: 2, pos: 'D' },
    { id: 23, name: 'T. Hernández', number: 22, pos: 'D' },
  ],
};

const FORM_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  W: { bg: 'hsl(142 71% 45% / 0.2)', text: 'hsl(142 71% 55%)', label: 'Win' },
  D: { bg: 'hsl(222 25% 22%)', text: 'hsl(222 15% 60%)', label: 'Draw' },
  L: { bg: 'hsl(0 84% 60% / 0.2)', text: 'hsl(0 84% 65%)', label: 'Loss' },
};

function deriveForm(stats: TeamStats): string[] {
  const results: string[] = [];
  let w = stats.wins;
  let d = stats.draws;
  let l = stats.losses;
  const total = Math.min(w + d + l, 5);
  for (let i = 0; i < total; i++) {
    if (w > 0 && (i % 2 === 0 || d === 0)) { results.push('W'); w--; }
    else if (l > 0 && i % 3 === 2) { results.push('L'); l--; }
    else if (d > 0) { results.push('D'); d--; }
    else if (w > 0) { results.push('W'); w--; }
    else if (l > 0) { results.push('L'); l--; }
  }
  return results;
}

export default function TeamProfilePage() {
  const params = useParams();
  const router = useRouter();
  const teamId = Number(params.id);
  const { t } = useLanguage();

  const [teamStanding, setTeamStanding] = useState<ApiStanding | null>(null);
  const [teamStats, setTeamStats] = useState<TeamStats | null>(null);
  const [upcomingFixtures, setUpcomingFixtures] = useState<LiveFixture[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'roster' | 'fixtures' | 'history'>('overview');

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [standings, fixtures] = await Promise.all([
      getWorldCupStandings(),
      getUpcomingFixtures(20),
    ]);

    let found: ApiStanding | null = null;
    for (const group of standings) {
      const match = group.find(s => s.team.id === teamId);
      if (match) { found = match; break; }
    }
    setTeamStanding(found);

    if (found) {
      setTeamStats({
        played: found.all.played,
        wins: found.all.win,
        draws: found.all.draw,
        losses: found.all.lose,
        goalsFor: found.all.goals.for,
        goalsAgainst: found.all.goals.against,
        goalDiff: found.goalsDiff,
        points: found.points,
        group: found.group,
        rank: found.rank,
      });
    }

    const teamFixtures = fixtures.filter(
      f => f.teams.home.id === teamId || f.teams.away.id === teamId
    );
    setUpcomingFixtures(teamFixtures);
    setLoading(false);
  }, [teamId]);

  useEffect(() => {
    if (teamId) fetchData();
  }, [teamId, fetchData]);

  const squad = MOCK_SQUADS[teamStanding?.team.name ?? ''] ?? MOCK_SQUADS.default;
  const byPosition = squad.reduce<Record<string, SquadPlayer[]>>((acc, p) => {
    (acc[p.pos] = acc[p.pos] || []).push(p);
    return acc;
  }, {});
  const sortedPositions = Object.keys(byPosition).sort(
    (a, b) => (POSITION_ORDER[a] ?? 9) - (POSITION_ORDER[b] ?? 9)
  );

  const form = teamStats ? deriveForm(teamStats) : [];

  const tabs = [
    { id: 'overview', label: t.teamProfile.overview, icon: BarChart2 },
    { id: 'roster', label: t.teamProfile.roster, icon: Users },
    { id: 'fixtures', label: t.teamProfile.fixtures, icon: Calendar },
    { id: 'history', label: t.teamProfile.history, icon: History },
  ] as const;

  const teamName = teamStanding?.team.name ?? 'Team';
  const teamLogo = teamStanding?.team.logo;
  const teamFlag = getFlag(teamName);

  return (
    <AppLayout activeRoute="/standings">
      <div className="space-y-5 pb-20 lg:pb-6">
        {/* Back Button */}
        <button
          onClick={() => router.back()}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
        >
          <ArrowLeft size={16} />
          <span>{t.teamProfile.back}</span>
        </button>

        {/* Hero Card */}
        <div
          className="rounded-2xl p-5 relative overflow-hidden"
          style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}
        >
          <div
            className="absolute inset-0 opacity-10 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse at 20% 50%, hsl(25 95% 53%), transparent 60%)' }}
          />

          {loading ? (
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl animate-pulse" style={{ background: 'hsl(222 25% 18%)' }} />
              <div className="space-y-2 flex-1">
                <div className="h-6 w-40 rounded-lg animate-pulse" style={{ background: 'hsl(222 25% 18%)' }} />
                <div className="h-4 w-24 rounded-lg animate-pulse" style={{ background: 'hsl(222 25% 18%)' }} />
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-4 relative">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'hsl(222 35% 15%)' }}
              >
                {teamLogo ? (
                  <img src={teamLogo} alt={teamName} className="w-12 h-12 object-contain" />
                ) : (
                  <span className="text-4xl">{teamFlag}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl font-black text-white tracking-tight">{teamName}</h1>
                {teamStats && (
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: 'hsl(25 95% 53% / 0.15)', color: 'hsl(25 95% 65%)' }}>
                      {teamStats.group}
                    </span>
                    <span className="text-xs text-slate-400">{t.teamProfile.rank}{teamStats.rank}</span>
                    <span className="text-xs font-bold text-white">{teamStats.points} {t.teamProfile.pts}</span>
                  </div>
                )}
              </div>
              {!loading && (
                <button onClick={fetchData} className="text-slate-600 hover:text-slate-400 transition-colors flex-shrink-0">
                  <RefreshCw size={14} />
                </button>
              )}
            </div>
          )}

          {/* Quick Stats Row */}
          {teamStats && !loading && (
            <div className="grid grid-cols-4 gap-2 mt-4">
              {[
                { label: t.teamProfile.played, value: teamStats.played, color: 'text-white' },
                { label: t.teamProfile.wins, value: teamStats.wins, color: 'text-green-400' },
                { label: t.teamProfile.goals, value: teamStats.goalsFor, color: 'text-cyan-400' },
                { label: t.teamProfile.points, value: teamStats.points, color: 'text-orange-400' },
              ].map(s => (
                <div key={s.label} className="rounded-xl p-2.5 text-center" style={{ background: 'hsl(222 35% 14%)' }}>
                  <div className={`text-xl font-black ${s.color}`}>{s.value}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-thin">
          {tabs.map(tab => {
            const TabIcon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium transition-all duration-150 flex-shrink-0"
                style={{
                  background: isActive ? 'hsl(25 95% 53%)' : 'hsl(222 35% 13%)',
                  color: isActive ? 'white' : 'hsl(222 15% 55%)',
                  border: isActive ? 'none' : '1px solid hsl(222 25% 16%)',
                }}
              >
                <TabIcon size={14} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="rounded-2xl animate-pulse" style={{ background: 'hsl(222 35% 11%)', height: 80 }} />
            ))}
          </div>
        ) : !teamStanding ? (
          <div className="rounded-2xl p-12 text-center" style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}>
            <div className="text-4xl mb-3">🔍</div>
            <p className="text-slate-300 font-semibold">{t.teamProfile.teamNotFound}</p>
            <p className="text-slate-500 text-sm mt-1">{t.teamProfile.teamNotInTournament}</p>
          </div>
        ) : (
          <>
            {/* OVERVIEW TAB */}
            {activeTab === 'overview' && teamStats && (
              <div className="space-y-4">
                {/* Recent Form */}
                <div className="rounded-2xl p-4" style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp size={15} className="text-orange-400" />
                    <span className="text-sm font-semibold text-white">{t.teamProfile.recentForm}</span>
                  </div>
                  {form.length > 0 ? (
                    <div className="flex items-center gap-2">
                      {form.map((result, i) => {
                        const style = FORM_COLORS[result] ?? FORM_COLORS.D;
                        return (
                          <div
                            key={i}
                            className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-black"
                            style={{ background: style.bg, color: style.text }}
                            title={style.label}
                          >
                            {result}
                          </div>
                        );
                      })}
                      <span className="text-xs text-slate-500 ml-2">{t.teamProfile.lastMatches} {form.length} {t.teamProfile.matches}</span>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">{t.teamProfile.noMatchesYet}</p>
                  )}
                </div>

                {/* Squad Stats */}
                <div className="rounded-2xl p-4" style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}>
                  <div className="flex items-center gap-2 mb-4">
                    <BarChart2 size={15} className="text-orange-400" />
                    <span className="text-sm font-semibold text-white">{t.teamProfile.squadStats}</span>
                  </div>
                  <div className="space-y-3">
                    {[
                      { label: t.teamProfile.goalsScored, value: teamStats.goalsFor, max: Math.max(teamStats.goalsFor, 10), color: 'hsl(142 71% 45%)' },
                      { label: t.teamProfile.goalsConceded, value: teamStats.goalsAgainst, max: Math.max(teamStats.goalsAgainst, 10), color: 'hsl(0 84% 60%)' },
                      { label: t.teamProfile.winRate, value: teamStats.played > 0 ? Math.round((teamStats.wins / teamStats.played) * 100) : 0, max: 100, color: 'hsl(25 95% 53%)', suffix: '%' },
                    ].map(stat => (
                      <div key={stat.label}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs text-slate-400">{stat.label}</span>
                          <span className="text-xs font-bold text-white">{stat.value}{stat.suffix ?? ''}</span>
                        </div>
                        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'hsl(222 25% 18%)' }}>
                          <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{
                              width: `${Math.min((stat.value / stat.max) * 100, 100)}%`,
                              background: stat.color,
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* W/D/L Breakdown */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: t.teamProfile.wins2, value: teamStats.wins, icon: '✅', color: 'hsl(142 71% 45%)' },
                    { label: t.teamProfile.draws, value: teamStats.draws, icon: '🤝', color: 'hsl(222 15% 60%)' },
                    { label: t.teamProfile.losses, value: teamStats.losses, icon: '❌', color: 'hsl(0 84% 60%)' },
                  ].map(s => (
                    <div key={s.label} className="rounded-xl p-3 text-center" style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}>
                      <div className="text-xl mb-1">{s.icon}</div>
                      <div className="text-2xl font-black" style={{ color: s.color }}>{s.value}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ROSTER TAB */}
            {activeTab === 'roster' && (
              <div className="space-y-4">
                {sortedPositions.map(pos => (
                  <div key={pos} className="rounded-2xl overflow-hidden" style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}>
                    <div className="px-4 py-2.5 flex items-center gap-2" style={{ background: 'hsl(222 35% 13%)', borderBottom: '1px solid hsl(222 25% 16%)' }}>
                      <Shield size={13} className="text-orange-400" />
                      <span className="text-xs font-bold text-white uppercase tracking-wider">{POSITION_LABEL[pos] ?? pos}</span>
                      <span className="ml-auto text-xs text-slate-600">{byPosition[pos].length} {t.teamProfile.players}</span>
                    </div>
                    <div className="divide-y" style={{ borderColor: 'hsl(222 25% 14%)' }}>
                      {byPosition[pos].map(player => (
                        <div key={player.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/3 transition-colors">
                          <span
                            className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-black flex-shrink-0"
                            style={{ background: 'hsl(222 35% 16%)', color: 'hsl(222 15% 55%)' }}
                          >
                            {player.number}
                          </span>
                          <span className="text-sm font-medium text-white flex-1">{player.name}</span>
                          <span
                            className="text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0"
                            style={{
                              background: pos === 'G' ? 'hsl(270 60% 50% / 0.15)' : pos === 'D' ? 'hsl(210 80% 50% / 0.15)' : pos === 'M' ? 'hsl(142 71% 45% / 0.15)' : 'hsl(25 95% 53% / 0.15)',
                              color: pos === 'G' ? 'hsl(270 60% 65%)' : pos === 'D' ? 'hsl(210 80% 65%)' : pos === 'M' ? 'hsl(142 71% 55%)' : 'hsl(25 95% 65%)',
                            }}
                          >
                            {pos}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <p className="text-xs text-slate-600 text-center">{t.teamProfile.rosterNote}</p>
              </div>
            )}

            {/* FIXTURES TAB */}
            {activeTab === 'fixtures' && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar size={15} className="text-orange-400" />
                  <span className="text-sm font-semibold text-white">{t.teamProfile.upcomingFixtures}</span>
                </div>
                {upcomingFixtures.length === 0 ? (
                  <div className="rounded-2xl p-10 text-center" style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}>
                    <div className="text-3xl mb-2">📅</div>
                    <p className="text-slate-400 text-sm">{t.teamProfile.noUpcomingFixtures}</p>
                  </div>
                ) : (
                  upcomingFixtures.map(fixture => {
                    const isHome = fixture.teams.home.id === teamId;
                    const opponent = isHome ? fixture.teams.away : fixture.teams.home;
                    const matchDate = new Date(fixture.fixture.date);
                    return (
                      <div
                        key={fixture.fixture.id}
                        className="rounded-2xl p-4 flex items-center gap-4"
                        style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: 'hsl(222 35% 16%)', color: 'hsl(222 15% 55%)' }}>
                              {isHome ? t.teamProfile.homeLabel : t.teamProfile.awayLabel}
                            </span>
                            <span className="text-xs text-slate-500">{fixture.league.round}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {opponent.logo ? (
                              <img src={opponent.logo} alt={opponent.name} className="w-6 h-6 object-contain" />
                            ) : (
                              <span className="text-lg">{getFlag(opponent.name)}</span>
                            )}
                            <span className="text-sm font-semibold text-white">vs {opponent.name}</span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-sm font-bold text-white">
                            {matchDate.toLocaleDateString([], { month: 'short', day: 'numeric' })}
                          </div>
                          <div className="text-xs text-slate-500">
                            {matchDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* HISTORY TAB */}
            {activeTab === 'history' && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  <History size={15} className="text-orange-400" />
                  <span className="text-sm font-semibold text-white">{t.teamProfile.tournamentHistory}</span>
                </div>

                {/* Current Tournament */}
                <div className="rounded-2xl p-4" style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Target size={14} className="text-cyan-400" />
                    <span className="text-sm font-semibold text-white">{t.teamProfile.worldCup2026}</span>
                    <span className="ml-auto text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: 'hsl(142 71% 45% / 0.15)', color: 'hsl(142 71% 55%)' }}>
                      {t.teamProfile.active}
                    </span>
                  </div>
                  {teamStats && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-xl p-3" style={{ background: 'hsl(222 35% 14%)' }}>
                        <div className="text-xs text-slate-500 mb-1">{t.teamProfile.groupStage}</div>
                        <div className="text-sm font-bold text-white">{teamStats.group}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{t.teamProfile.rank}{teamStats.rank} · {teamStats.points} {t.teamProfile.pts}</div>
                      </div>
                      <div className="rounded-xl p-3" style={{ background: 'hsl(222 35% 14%)' }}>
                        <div className="text-xs text-slate-500 mb-1">{t.teamProfile.goalDifference}</div>
                        <div className={`text-sm font-bold ${teamStats.goalDiff > 0 ? 'text-green-400' : teamStats.goalDiff < 0 ? 'text-red-400' : 'text-white'}`}>
                          {teamStats.goalDiff > 0 ? '+' : ''}{teamStats.goalDiff}
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5">{teamStats.goalsFor} {t.teamProfile.scored} · {teamStats.goalsAgainst} {t.teamProfile.conceded}</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Past Tournaments */}
                <div className="rounded-2xl overflow-hidden" style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}>
                  <div className="px-4 py-2.5 flex items-center gap-2" style={{ background: 'hsl(222 35% 13%)', borderBottom: '1px solid hsl(222 25% 16%)' }}>
                    <Swords size={13} className="text-slate-400" />
                    <span className="text-xs font-bold text-white uppercase tracking-wider">{t.teamProfile.previousWorldCups}</span>
                  </div>
                  {[
                    { year: 2022, host: 'Qatar', stage: 'Group Stage' },
                    { year: 2018, host: 'Russia', stage: 'Round of 16' },
                    { year: 2014, host: 'Brazil', stage: 'Quarter-finals' },
                    { year: 2010, host: 'South Africa', stage: 'Group Stage' },
                  ].map((entry, i) => (
                    <div
                      key={entry.year}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-white/3 transition-colors"
                      style={{ borderBottom: i < 3 ? '1px solid hsl(222 25% 13%)' : 'none' }}
                    >
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-black flex-shrink-0"
                        style={{ background: 'hsl(222 35% 15%)', color: 'hsl(222 15% 55%)' }}
                      >
                        {entry.year}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-white">{entry.host}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{entry.stage}</div>
                      </div>
                      <div
                        className="text-xs px-2 py-1 rounded-lg font-medium"
                        style={{
                          background: entry.stage.includes('Final') ? 'hsl(25 95% 53% / 0.15)' : entry.stage.includes('Quarter') ? 'hsl(189 94% 51% / 0.1)' : 'hsl(222 25% 18%)',
                          color: entry.stage.includes('Final') ? 'hsl(25 95% 65%)' : entry.stage.includes('Quarter') ? 'hsl(189 94% 65%)' : 'hsl(222 15% 55%)',
                        }}
                      >
                        {entry.stage}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-600 text-center">{t.teamProfile.historicalNote}</p>
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
